/**
 * PermissionGate Component
 * Conditionally renders children based on user permissions
 */

import React from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { Box, Typography, Paper, Alert } from '@mui/material';
import { Lock } from '@mui/icons-material';

/**
 * PermissionGate - Wrap components that require specific permissions
 * 
 * @param {string} permission - Single permission required
 * @param {string[]} permissions - Array of permissions (use with anyOf or allOf)
 * @param {boolean} anyOf - If true, user needs ANY of the permissions
 * @param {boolean} allOf - If true, user needs ALL of the permissions (default if multiple)
 * @param {string} role - Required minimum role (owner, admin, manager, employee)
 * @param {React.ReactNode} children - Content to render if authorized
 * @param {React.ReactNode} fallback - Content to render if unauthorized (optional)
 * @param {boolean} showAccessDenied - Show "Access Denied" message if unauthorized
 * @param {boolean} hide - If true, renders nothing when unauthorized (default)
 */
export function PermissionGate({
  permission,
  permissions,
  anyOf = false,
  allOf = true,
  role,
  children,
  fallback = null,
  showAccessDenied = false,
  hide = true,
}) {
  const { can, canAll, canAny, isAtLeast } = usePermissions();

  // Determine if user has access
  let hasAccess = true;

  if (role) {
    hasAccess = isAtLeast(role);
  }

  if (hasAccess && permission) {
    hasAccess = can(permission);
  }

  if (hasAccess && permissions?.length) {
    if (anyOf) {
      hasAccess = canAny(permissions);
    } else {
      hasAccess = canAll(permissions);
    }
  }

  // Render based on access
  if (hasAccess) {
    return <>{children}</>;
  }

  // If fallback provided, render it
  if (fallback) {
    return <>{fallback}</>;
  }

  // Show access denied message
  if (showAccessDenied) {
    return <AccessDenied />;
  }

  // Hide completely
  if (hide) {
    return null;
  }

  return null;
}

/**
 * AccessDenied Component - Shown when user doesn't have permission
 */
export function AccessDenied({ message }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 200,
        p: 3,
      }}
    >
      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          maxWidth: 400,
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
        }}
      >
        <Lock sx={{ fontSize: 48, color: '#ef4444', mb: 2 }} />
        <Typography variant="h6" gutterBottom color="error">
          Δεν έχετε πρόσβαση
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {message || 'Δεν έχετε τα απαραίτητα δικαιώματα για να δείτε αυτό το περιεχόμενο.'}
        </Typography>
      </Paper>
    </Box>
  );
}

/**
 * RequireRole - Simple wrapper that requires a minimum role
 */
export function RequireRole({ role, children, showAccessDenied = false }) {
  return (
    <PermissionGate role={role} showAccessDenied={showAccessDenied}>
      {children}
    </PermissionGate>
  );
}

/**
 * OwnerOnly - Only visible to organization owners
 */
export function OwnerOnly({ children, showAccessDenied = false }) {
  return (
    <PermissionGate role="owner" showAccessDenied={showAccessDenied}>
      {children}
    </PermissionGate>
  );
}

/**
 * AdminOnly - Only visible to admins and owners
 */
export function AdminOnly({ children, showAccessDenied = false }) {
  return (
    <PermissionGate role="admin" showAccessDenied={showAccessDenied}>
      {children}
    </PermissionGate>
  );
}

/**
 * ManagerOnly - Only visible to managers and above
 */
export function ManagerOnly({ children, showAccessDenied = false }) {
  return (
    <PermissionGate role="manager" showAccessDenied={showAccessDenied}>
      {children}
    </PermissionGate>
  );
}

/**
 * AlertIfNoPermission - Shows an alert if user lacks permission
 */
export function AlertIfNoPermission({ permission, role, message }) {
  const { can, isAtLeast } = usePermissions();

  let hasAccess = true;
  if (role) hasAccess = isAtLeast(role);
  if (hasAccess && permission) hasAccess = can(permission);

  if (hasAccess) return null;

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      {message || 'Μερικές λειτουργίες δεν είναι διαθέσιμες λόγω περιορισμένων δικαιωμάτων.'}
    </Alert>
  );
}

export default PermissionGate;

